import { ref, uploadBytes, getBytes, listAll, deleteObject } from 'firebase/storage';
import { storage } from './config';

const MAGIC = 'TILE';
const HEADER_SIZE = 48; // 4 magic + 4 resX + 4 resY + 4 step + 8*4 bbox

function tileFileName(bbox, step) {
  // Współrzędne w metrach, zaokrąglone do 1 m
  const minX = Math.round(bbox.minX);
  const minY = Math.round(bbox.minY);
  const maxX = Math.round(bbox.maxX);
  const maxY = Math.round(bbox.maxY);
  return `tile_${minX}_${minY}_${maxX}_${maxY}_${step}.bin`;
}

function parseTileFileName(name) {
  const m = name.match(/^tile_(-?\d+)_(-?\d+)_(-?\d+)_(-?\d+)_(\d+(?:\.\d+)?)\.bin$/);
  if (!m) return null;
  return {
    bbox: { minX: +m[1], minY: +m[2], maxX: +m[3], maxY: +m[4] },
    step: +m[5],
    fileName: name,
  };
}

function packTile(grid, bbox, step) {
  const resY = grid.length;
  const resX = grid[0].length;
  const totalSize = HEADER_SIZE + resX * resY * 4;
  const buf = new ArrayBuffer(totalSize);
  const view = new DataView(buf);

  for (let i = 0; i < 4; i++) view.setUint8(i, MAGIC.charCodeAt(i));
  view.setUint32(4, resX, true);
  view.setUint32(8, resY, true);
  view.setFloat32(12, step, true);
  view.setFloat64(16, bbox.minX, true);
  view.setFloat64(24, bbox.maxX, true);
  view.setFloat64(32, bbox.minY, true);
  view.setFloat64(40, bbox.maxY, true);

  let offset = HEADER_SIZE;
  for (let j = 0; j < resY; j++) {
    for (let i = 0; i < resX; i++) {
      view.setFloat32(offset, grid[j][i] ?? 0, true);
      offset += 4;
    }
  }
  return buf;
}

function unpackTile(buf) {
  const view = new DataView(buf);
  const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (magic !== MAGIC) throw new Error('Niewłaściwy format tile NMT (zły magic)');

  const resX = view.getUint32(4, true);
  const resY = view.getUint32(8, true);
  const step = view.getFloat32(12, true);
  const bbox = {
    minX: view.getFloat64(16, true),
    maxX: view.getFloat64(24, true),
    minY: view.getFloat64(32, true),
    maxY: view.getFloat64(40, true),
  };

  const grid = [];
  let offset = HEADER_SIZE;
  for (let j = 0; j < resY; j++) {
    const row = new Array(resX);
    for (let i = 0; i < resX; i++) {
      row[i] = view.getFloat32(offset, true);
      offset += 4;
    }
    grid.push(row);
  }

  // Odtwórz xCoords/yCoords z bbox + wymiarów
  const stepX = (bbox.maxX - bbox.minX) / (resX - 1);
  const stepY = (bbox.maxY - bbox.minY) / (resY - 1);
  const xCoords = Array.from({ length: resX }, (_, i) => bbox.minX + i * stepX);
  const yCoords = Array.from({ length: resY }, (_, j) => bbox.minY + j * stepY);

  return { grid, bbox, step, resX, resY, xCoords, yCoords };
}

/**
 * Listuje wszystkie tile NMT zapisane w Storage dla projektu.
 * Zwraca metadata z nazwy pliku — bez ładowania samych danych.
 */
export async function listTiles(projectId) {
  if (!projectId) return [];
  try {
    const { items } = await listAll(ref(storage, `nmt-tiles/${projectId}`));
    return items
      .map(item => parseTileFileName(item.name))
      .filter(t => t !== null);
  } catch (err) {
    console.warn('Listowanie tile NMT błąd:', err);
    return [];
  }
}

/**
 * Pobiera pełne dane tile (grid + bbox + xCoords/yCoords).
 */
export async function getTile(projectId, fileName) {
  const fileRef = ref(storage, `nmt-tiles/${projectId}/${fileName}`);
  const buf = await getBytes(fileRef);
  return unpackTile(buf);
}

/**
 * Zapisuje tile do Storage.
 */
export async function saveTile(projectId, bbox, step, grid) {
  const fileName = tileFileName(bbox, step);
  const fileRef = ref(storage, `nmt-tiles/${projectId}/${fileName}`);
  const buf = packTile(grid, bbox, step);
  await uploadBytes(fileRef, buf, { contentType: 'application/octet-stream' });
  return fileName;
}

/**
 * Usuwa tile ze Storage.
 */
export async function deleteTile(projectId, fileName) {
  const fileRef = ref(storage, `nmt-tiles/${projectId}/${fileName}`);
  await deleteObject(fileRef);
}

/**
 * Bilinearne próbkowanie tile w punkcie (px, py) PL-2000.
 * Zwraca rzędną m n.p.m. lub null gdy punkt poza tile.
 */
export function bilinearSampleTile(tile, px, py) {
  const { xCoords, yCoords, grid, resX, resY } = tile;
  const minX = xCoords[0];
  const maxX = xCoords[resX - 1];
  const minY = yCoords[0];
  const maxY = yCoords[resY - 1];

  // Tolerancja na granicy (zaokrąglenia floata)
  const eps = 1e-3;
  if (px < minX - eps || px > maxX + eps || py < minY - eps || py > maxY + eps) {
    return null;
  }

  const stepX = (maxX - minX) / (resX - 1);
  const stepY = (maxY - minY) / (resY - 1);

  let i = Math.floor((px - minX) / stepX);
  let j = Math.floor((py - minY) / stepY);
  if (i < 0) i = 0;
  if (j < 0) j = 0;
  if (i >= resX - 1) i = resX - 2;
  if (j >= resY - 1) j = resY - 2;

  const u = (px - (minX + i * stepX)) / stepX;
  const v = (py - (minY + j * stepY)) / stepY;

  return (
    (1 - u) * (1 - v) * grid[j][i] +
    u * (1 - v) * grid[j][i + 1] +
    (1 - u) * v * grid[j + 1][i] +
    u * v * grid[j + 1][i + 1]
  );
}

/**
 * Znajduje pierwszy tile pokrywający w pełni dany bbox.
 * Najmniejszy pasujący (preferuje dokładniejszą rozdzielczość gdy kilka pasuje).
 */
export function findCoveringTile(tiles, bbox) {
  const eps = 1e-3;
  const matching = tiles.filter(t =>
    t.bbox.minX <= bbox.minX + eps &&
    t.bbox.maxX >= bbox.maxX - eps &&
    t.bbox.minY <= bbox.minY + eps &&
    t.bbox.maxY >= bbox.maxY - eps
  );
  if (matching.length === 0) return null;
  // Najmniejszy step = najlepsza dokładność
  matching.sort((a, b) => a.step - b.step);
  return matching[0];
}
